// Cluster ZUWEISEN (C9) — „Diese Nummer haben wir noch nicht — übernehmen?"
//
// Der Dialog beantwortet genau eine Frage: WEM gehört die in der Mail gefundene
// Nummer (bzw. Adresse)? Bei Firmenbezug stehen Firma, jeder bestehende
// Ansprechpartner und „neuer Ansprechpartner" zur Wahl; bei einem Privatkontakt
// bleibt nur privat oder geschäftlich.
//
// B2: keine Fluent-Formularkomponente — native Controls im kvp-rd-Look.
// Reine Logik (Zielliste, Payloads, „Später"): mailNummerZuordnen.ts.

import * as React from "react";
import { PersonAdd20Regular } from "@fluentui/react-icons";
import { useWindowManager, type OpenWindowConfig } from "../windowManager/WindowManagerContext";
import { LabeledInput, StatusSegmented } from "../forms";
import { contactCommunicationService } from "../../services/contactCommunicationService";
import { attachMenschen } from "../../services/menschenFuersUnternehmenService";
import { fetchCleanupFirma } from "../../services/ansprechpartnerCleanupService";
import { reportMailError } from "../../services/mailError";
import type { KommArt } from "./mailContactAssign";
import {
    buildNeuerApItem,
    buildNummerPayload,
    merkeSpaeter,
    standardZiel,
    zieleFuerNummer,
    type C9Art,
    type FehlenderEintrag,
    type FirmaPerson,
    type NummerBezug,
    type NummerZiel,
} from "./mailNummerZuordnen";

const AUB = "#4d2051";

/** Beschriftung je Art — dieselbe Sprache wie in der Hinweisleiste. */
const ART_LABEL: Record<C9Art, string> = {
    phone: "Telefon",
    mobile: "Mobil",
    fax: "Fax",
    email: "E-Mail",
};

export interface OpenMailNummerZuordnenParams {
    /** Postfach + Nachricht — nur für das „Später"-Gedächtnis (sessionStorage). */
    mailbox: string;
    messageId: string;
    /** Der gefundene, noch nicht hinterlegte Eintrag. */
    eintrag: FehlenderEintrag;
    /** Kontakt- und Firmenbezug, aus dem die Zielliste entsteht. */
    bezug: NummerBezug;
    /** Wird nach erfolgreichem Übernehmen bzw. nach „Später" gerufen. */
    onErledigt?: (ergebnis: { uebernommen: boolean; ziel?: NummerZiel }) => void;
}

const MailNummerZuordnenWindow: React.FC<OpenMailNummerZuordnenParams & { windowId: string }> = ({
    windowId,
    mailbox,
    messageId,
    eintrag,
    bezug,
    onErledigt,
}) => {
    const { closeWindow } = useWindowManager();
    const [personen, setPersonen] = React.useState<FirmaPerson[]>([]);
    const [laedt, setLaedt] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [fehler, setFehler] = React.useState<string | null>(null);
    const [art, setArt] = React.useState<C9Art>(eintrag.kind);
    const [kommArt, setKommArt] = React.useState<KommArt>("privat");
    const [zielKey, setZielKey] = React.useState<string | null>(null);
    const [neuLast, setNeuLast] = React.useState("");
    const [neuFirst, setNeuFirst] = React.useState("");
    const [filter, setFilter] = React.useState("");

    const firmaId = bezug.firma?.id ?? null;

    React.useEffect(() => {
        if (firmaId == null) return;
        let abgebrochen = false;
        setLaedt(true);
        void fetchCleanupFirma(firmaId)
            .then((d) => {
                if (abgebrochen) return;
                setPersonen(
                    (d.persons ?? []).map((p) => ({
                        personId: p.personId,
                        lastname: p.lastname,
                        firstName: p.firstName,
                    })),
                );
            })
            .catch(() => {
                // Ohne die Personenliste bleiben Firma und „neuer AP" wählbar —
                // der Dialog ist dann ärmer, aber nicht kaputt.
                if (!abgebrochen) setPersonen([]);
            })
            .finally(() => {
                if (!abgebrochen) setLaedt(false);
            });
        return () => {
            abgebrochen = true;
        };
    }, [firmaId]);

    const ziele = React.useMemo(() => zieleFuerNummer(bezug, personen), [bezug, personen]);
    const key = (z: NummerZiel) => (z.art === "ap-neu" ? "ap-neu" : `${z.art}-${z.contactId}`);
    const gewaehlt = React.useMemo(() => {
        const treffer = ziele.find((z) => key(z) === zielKey);
        return treffer ?? standardZiel(ziele, bezug);
    }, [ziele, zielKey, bezug]);

    // Ab acht Personen wird die Liste unübersichtlich → Filterfeld.
    const sichtbareZiele = React.useMemo(() => {
        const f = filter.trim().toLowerCase();
        if (!f) return ziele;
        return ziele.filter((z) => z.art !== "ap" || z.label.toLowerCase().includes(f));
    }, [ziele, filter]);

    const schliessen = () => closeWindow(windowId);

    const spaeter = () => {
        merkeSpaeter(mailbox, messageId, [eintrag.value]);
        onErledigt?.({ uebernommen: false });
        schliessen();
    };

    const uebernehmen = async () => {
        if (!gewaehlt || busy) return;
        setBusy(true);
        setFehler(null);
        const daten = { kind: art, value: eintrag.value };
        try {
            if (gewaehlt.art === "ap-neu") {
                if (!neuLast.trim()) throw new Error("Der Nachname ist erforderlich.");
                const created = await attachMenschen([
                    buildNeuerApItem(gewaehlt.firmaId, { lastName: neuLast, firstName: neuFirst }, daten),
                ]);
                // Fax kennt attach-menschen nicht — dann wird die Nummer direkt
                // am frisch angelegten Kontakt nachgetragen.
                const neueId = created[0]?.contactId;
                if (art === "fax" && neueId) {
                    await contactCommunicationService.attachCommunication(neueId, {
                        type: "fax",
                        value: eintrag.value,
                        label: "Geschäftlich",
                        kind: "geschaeftlich",
                        companyId: gewaehlt.firmaId,
                        isDefault: true,
                    });
                }
            } else {
                const ziel = buildNummerPayload(gewaehlt, daten, { art: kommArt });
                if (!ziel) throw new Error("Für dieses Ziel gibt es nichts zu speichern.");
                await contactCommunicationService.attachCommunication(ziel.contactId, ziel.payload);
            }
            onErledigt?.({ uebernommen: true, ziel: gewaehlt });
            schliessen();
        } catch (error: unknown) {
            const m = String((error as { message?: unknown })?.message ?? "");
            const fallbackMessage = m.includes("403")
                ? "Dir fehlt das Schreibrecht für diesen Kontakt."
                : "Die Angabe konnte nicht gespeichert werden.";
            setFehler(
                reportMailError({
                    area: "mail",
                    action: "assign",
                    error,
                    fallbackMessage,
                }),
            );
            setBusy(false);
        }
    };

    const bereit =
        gewaehlt != null && (gewaehlt.art !== "ap-neu" || neuLast.trim() !== "");

    return (
        <div style={S.root}>
            <div style={S.head}>
                <div style={S.title}>Gefundene Kontaktdaten übernehmen</div>
                <div style={S.hint}>
                    <strong>{eintrag.display}</strong>{" "}
                    {eintrag.source === "signature" ? "(aus der Signatur)" : "(aus dem Absender)"}
                    {!eintrag.confident && " — unsicher erkannt"}
                </div>
            </div>

            <div style={S.body}>
                <div style={S.block}>
                    <div style={S.label}>Art der Angabe</div>
                    <StatusSegmented
                        ariaLabel="Art der Angabe"
                        value={ART_LABEL[art]}
                        options={[ART_LABEL.phone, ART_LABEL.mobile, ART_LABEL.fax, ART_LABEL.email]}
                        onChange={(v) => {
                            const treffer = (Object.keys(ART_LABEL) as C9Art[]).find(
                                (k) => ART_LABEL[k] === v,
                            );
                            if (treffer) setArt(treffer);
                        }}
                    />
                </div>

                {bezug.firma ? (
                    <div style={S.block}>
                        <div style={S.label}>Wem gehört die Angabe?</div>
                        {laedt && <div style={S.note}>Ansprechpartner werden geladen …</div>}
                        {personen.length >= 8 && (
                            <input
                                style={S.filter}
                                type="text"
                                placeholder="Ansprechpartner filtern …"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                            />
                        )}
                        <div role="radiogroup" aria-label="Wem gehört die Angabe?" style={S.liste}>
                            {sichtbareZiele.map((z) => (
                                <label key={key(z)} style={S.option}>
                                    <input
                                        type="radio"
                                        name="c9-ziel"
                                        checked={gewaehlt != null && key(gewaehlt) === key(z)}
                                        onChange={() => setZielKey(key(z))}
                                    />{" "}
                                    <span>
                                        {z.art === "firma" ? (
                                            <>
                                                Der Firma <strong>{z.label}</strong>
                                            </>
                                        ) : (
                                            z.label
                                        )}
                                    </span>
                                </label>
                            ))}
                        </div>
                        {gewaehlt?.art === "ap-neu" && (
                            <div style={S.sub}>
                                <LabeledInput label="Vorname" name="c9-first" value={neuFirst} onChange={setNeuFirst} />
                                <LabeledInput label="Nachname" name="c9-last" required value={neuLast} onChange={setNeuLast} />
                                <div style={S.note}>
                                    Der neue Ansprechpartner erbt FO/BO von der Firma; seine
                                    geschäftlichen Angaben werden ihr zugeordnet.
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={S.block}>
                        <div style={S.label}>Privat oder geschäftlich?</div>
                        <StatusSegmented
                            ariaLabel="Privat oder geschäftlich"
                            value={kommArt === "geschaeftlich" ? "geschäftlich" : "privat"}
                            options={["privat", "geschäftlich"]}
                            onChange={(v) => setKommArt(v === "geschäftlich" ? "geschaeftlich" : "privat")}
                        />
                        <div style={S.note}>
                            Hinterlegt bei <strong>{bezug.kontakt?.displayName ?? "dem Kontakt"}</strong>.
                        </div>
                    </div>
                )}

                {fehler && (
                    <div style={S.error} role="alert">
                        {fehler}
                    </div>
                )}
            </div>

            <div style={S.footer}>
                <button type="button" style={S.ghost} onClick={schliessen} disabled={busy}>
                    Abbrechen
                </button>
                <button type="button" style={S.link} onClick={spaeter} disabled={busy}>
                    Später
                </button>
                <span style={{ flex: 1 }} />
                <button
                    type="button"
                    style={S.primary}
                    onClick={() => void uebernehmen()}
                    disabled={busy || !bereit}
                >
                    {busy ? "Wird gespeichert …" : "Übernehmen"}
                </button>
            </div>
        </div>
    );
};

const S: Record<string, React.CSSProperties> = {
    root: { display: "flex", flexDirection: "column", height: "100%", background: "#fff", fontSize: 13, color: "#241522" },
    head: { padding: "12px 16px 8px", borderBottom: "1px solid #ece7f0" },
    title: { fontWeight: 700, fontSize: 15, color: AUB },
    hint: { marginTop: 4, color: "#6b5a66", lineHeight: 1.4 },
    body: { flex: 1, overflowY: "auto", padding: "12px 16px", minHeight: 0, display: "flex", flexDirection: "column", gap: 14 },
    block: { display: "flex", flexDirection: "column", gap: 8 },
    label: { fontWeight: 600, fontSize: 12.5, color: AUB },
    liste: { display: "flex", flexDirection: "column", gap: 6 },
    option: { display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer", lineHeight: 1.4 },
    sub: { display: "flex", flexDirection: "column", gap: 8, paddingLeft: 10, borderLeft: "2px solid #ece7f0" },
    filter: { border: "1px solid #ddd3dc", borderRadius: 8, padding: "7px 10px", fontSize: 13 },
    note: { color: "#6b5a66", fontSize: 12, lineHeight: 1.4 },
    error: { color: "#b3261e", background: "#fdecea", border: "1px solid #f5c6cb", borderRadius: 6, padding: "6px 10px" },
    footer: { display: "flex", gap: 8, alignItems: "center", padding: "10px 16px", borderTop: "1px solid #ece7f0" },
    ghost: { border: "1px solid #ece7f0", background: "#fff", color: "#6b5a66", borderRadius: 6, padding: "6px 12px", cursor: "pointer" },
    link: { border: "none", background: "transparent", color: AUB, textDecoration: "underline", cursor: "pointer", padding: "6px 2px" },
    primary: { display: "inline-flex", alignItems: "center", gap: 6, border: `1px solid ${AUB}`, background: AUB, color: "#fff", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontWeight: 600 },
};

/** Öffnet die Zuordnung als kleines Light-Fenster (WindowManager). */
export const openMailNummerZuordnen = (
    openWindow: (config: OpenWindowConfig) => void,
    params: OpenMailNummerZuordnenParams,
): string => {
    const windowId = `mail-nummer-${Date.now()}`;
    openWindow({
        id: windowId,
        title: "Kontaktdaten übernehmen",
        icon: React.createElement(PersonAdd20Regular),
        initialSize: { width: 440, height: 460 },
        center: true,
        render: () => React.createElement(MailNummerZuordnenWindow, { windowId, ...params }),
    });
    return windowId;
};

export default MailNummerZuordnenWindow;
